import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { FocalArea } from '../models/focal-area.model';
import { DetectionResult } from '../models/detection-result.interface';
import { environment } from '../../../../../environments/environment';
import { LayerOptimizationsDto } from '../../../../../../../api/src/creative-unit-package/models/layer/layer-optimization.dto';
import { LayerType } from '../../../../../../../api/src/creative-unit-package/models/layer/layer.dto';

@Component({
  selector: 'app-focal-area-preview',
  templateUrl: './focal-area-preview.component.html',
  styleUrls: ['./focal-area-preview.component.scss']
})
export class FocalAreaPreviewComponent implements OnChanges {
	@Input() imageUrl: string | null = null;
	@Input() focalArea: FocalArea | null = null;
	@Input() previewMode: string = 'original';
	@Input() customAspectRatio: string = '16:9';
	@Input() imageNaturalWidth: number = 0;
	@Input() imageNaturalHeight: number = 0;
	@Input() autoDetectMode: boolean = false;
	@Input() filterTags: string[] = [];

	@Output() detectionResult = new EventEmitter<DetectionResult>();

	optimizedImageUrl: string | null = null;
	previewWidth: number = 0;
	previewHeight: number = 0;
	displayAspectRatio: string = '';

	// Track the URL we're currently fetching/displaying to prevent infinite loops
	private currentFetchUrl: string | null = null;
	private isFetching: boolean = false;

	// Cache previous values to detect actual changes
	private previousFilterTags: string[] = [];
	private lastGeneratedParams: string = '';

	ngOnChanges(changes: SimpleChanges): void {
		// Check if we have any meaningful changes
		let shouldRegenerate = false;

		// Check simple value changes
		if (changes['focalArea'] || changes['previewMode'] || changes['customAspectRatio'] || changes['imageUrl'] || changes['autoDetectMode']) {
			shouldRegenerate = true;
		}

		// Check filterTags array for actual content changes (not just reference changes)
		if (changes['filterTags'] && this.filterTags) {
			const currentTags = [...this.filterTags].sort().join(',');
			const previousTags = [...this.previousFilterTags].sort().join(',');
			if (currentTags !== previousTags) {
				this.previousFilterTags = [...this.filterTags];
				shouldRegenerate = true;
			}
		}

		if (shouldRegenerate) {
			this.generateOptimizedUrl();
		}
	}

	private generateOptimizedUrl(): void {
		// Handle original mode (no optimization)
		if (!this.imageUrl || this.previewMode === 'original') {
			this.optimizedImageUrl = this.imageUrl;
			this.displayAspectRatio = 'original';
			this.lastGeneratedParams = 'original';
			return;
		}

		// In auto-detect mode, we don't need a focal area selected
		// In manual mode, we need a focal area
		if (!this.autoDetectMode && !this.focalArea) {
			this.optimizedImageUrl = this.imageUrl;
			this.displayAspectRatio = this.previewMode;
			this.lastGeneratedParams = 'no-focal-area';
			return;
		}

		// Create a fingerprint of the current parameters to detect if anything meaningful changed
		const focalAreaFingerprint = this.focalArea
			? `${this.focalArea.boundingBox.x},${this.focalArea.boundingBox.y},${this.focalArea.boundingBox.width},${this.focalArea.boundingBox.height},${this.focalArea.cropMode}`
			: 'auto';
		const filterTagsFingerprint = this.filterTags.sort().join(',');
		const currentParams = `${this.previewMode}|${this.customAspectRatio}|${this.autoDetectMode}|${focalAreaFingerprint}|${filterTagsFingerprint}|${this.imageUrl}`;

		// If parameters haven't changed, skip regeneration
		if (currentParams === this.lastGeneratedParams) {
			return;
		}

		this.lastGeneratedParams = currentParams;

		// Get target dimensions from aspect ratio
		let targetWidth: number;
		let targetHeight: number;

		if (this.previewMode === 'custom') {
			const [w, h] = this.customAspectRatio.split(':').map(parseFloat);
			// Use a reasonable preview size (800px width)
			targetWidth = 800;
			targetHeight = Math.round(targetWidth * (h / w));
			this.displayAspectRatio = this.customAspectRatio;
		} else {
			const [w, h] = this.previewMode.split(':').map(parseFloat);
			// Use a reasonable preview size (800px width)
			targetWidth = 800;
			targetHeight = Math.round(targetWidth * (h / w));
			this.displayAspectRatio = this.previewMode;
		}

		this.previewWidth = targetWidth;
		this.previewHeight = targetHeight;

		let optimizations: LayerOptimizationsDto;

		// First, let's just optimize the image path itself to be smaller / lower quality to speed up loading.
		let previewOptimizations: LayerOptimizationsDto = {
			provider: 'cloudinary',
			resize: {
				mode: 'fit' as any,
				width: (targetWidth * 2).toString(),
				height: (targetHeight * 2).toString(),
				gravity: undefined,
				x: null,
				y: null,
				zoom: null,
				zoomGravity: null
			},
			eTrim: {
				enabled: false,
				tolerance: 0
			}
		};

		const optimizedUrl = LayerOptimizationsDto.optimizeAsset(
			this.imageUrl,
			'image' as LayerType,
			previewOptimizations,
			environment.cloudinaryCloudName,
			environment.taskerEngineUrl,
			environment.apiUrl
		);

		if (this.autoDetectMode) {
			// Auto-detection mode: Use autoTargetCropEnabled instead of specific coordinates
			optimizations = {
				provider: 'tasker',
				targetCrop: {
					autoTargetCropEnabled: true,
					width: targetWidth.toString(),
					height: targetHeight.toString(),
					cropMode: 'contain', // Default to contain, backend will use focal area's cropMode
					aspectPriority: this.focalArea?.aspectPriority, // Pass through aspectPriority from focal area
					margin: undefined,
					tx: undefined,
					ty: undefined,
					tw: undefined,
					th: undefined
				},
				targetFilterTags: this.filterTags.length > 0 ? this.filterTags : undefined,
				resize: {
					mode: 'fill' as any,
					width: targetWidth.toString(),
					height: targetHeight.toString(),
					gravity: undefined,
					x: null,
					y: null,
					zoom: null,
					zoomGravity: null
				},
				eTrim: {
					enabled: false,
					tolerance: 0
				}
			};
		} else {
			// Manual mode: Use specific focal area coordinates
			const cropMode = this.focalArea!.cropMode || 'cover';
			const aspectPriority = this.focalArea!.aspectPriority; // Get aspectPriority from focal area
			optimizations = {
				provider: 'tasker',
				targetCrop: {
					tx: (this.focalArea!.boundingBox.x / 100).toString(),
					ty: (this.focalArea!.boundingBox.y / 100).toString(),
					tw: (this.focalArea!.boundingBox.width / 100).toString(),
					th: (this.focalArea!.boundingBox.height / 100).toString(),
					width: targetWidth.toString(),
					height: targetHeight.toString(),
					cropMode: cropMode,
					aspectPriority: aspectPriority, // Pass through aspectPriority
					margin: '0'
				},
				resize: {
					mode: 'fill' as any,
					width: targetWidth.toString(),
					height: targetHeight.toString(),
					gravity: 'center' as any,
					x: null,
					y: null,
					zoom: null,
					zoomGravity: null
				},
				eTrim: {
					enabled: false,
					tolerance: 0
				}
			};
		}

		console.log('Optimizations:', optimizations);
		console.log('Image Natural Size:', this.imageNaturalWidth, this.imageNaturalHeight);
		console.log('Focal Area:', this.focalArea);

		console.log('Calling optimizeAsset with:');
		console.log('  assetPath:', this.imageUrl);
		console.log('  type:', 'image');
		console.log('  optimizations:', optimizations);
		console.log('  bucketId:', environment.cloudinaryCloudName);
		console.log('  taskerEngineUrl:', environment.taskerEngineUrl);

		// Generate optimized URL using LayerOptimizationsDto.optimizeAsset
		const generatedUrl = LayerOptimizationsDto.optimizeAsset(
			optimizedUrl,
			'image' as LayerType,
			optimizations,
			environment.cloudinaryCloudName,
			environment.taskerEngineUrl,
			environment.apiUrl
		);

		console.log('Optimized URL:', generatedUrl);

		// If auto-detect mode is enabled, fetch the image to read headers
		if (this.autoDetectMode) {
			// Only fetch if this is a new URL or we're not currently fetching
			if (generatedUrl !== this.currentFetchUrl && !this.isFetching) {
				this.fetchImageWithHeaders(generatedUrl);
			}
		} else {
			this.currentFetchUrl = null;
			this.optimizedImageUrl = generatedUrl;
		}
	}

	/**
	 * Fetch image using fetch API to access response headers
	 * Used in auto-detect mode to read detection information from headers
	 */
	private async fetchImageWithHeaders(url: string): Promise<void> {
		// Mark that we're fetching this URL
		this.currentFetchUrl = url;
		this.isFetching = true;

		try {
			const response = await fetch(url);

			if (!response.ok) {
				console.error('Failed to fetch image:', response.status, response.statusText);
				this.handleFetchError('Network error');
				return;
			}

			// Parse detection headers
			const usedAutoDetect = response.headers.get('X-Auto-Target-Crop') === 'true';
			const selectedFocalArea = response.headers.get('X-Selected-Focal-Area') || undefined;
			const fallbackReason = response.headers.get('X-Fallback-Reason') || undefined;

			console.log('Detection Headers:', {
				usedAutoDetect,
				selectedFocalArea,
				fallbackReason
			});

			// Create detection result
			const result: DetectionResult = {
				aspectRatio: this.displayAspectRatio,
				selectedFocalArea,
				fallbackReason,
				usedAutoDetect
			};

			// Emit detection result to parent
			this.detectionResult.emit(result);

			// Convert response to blob and create object URL for display
			const blob = await response.blob();
			const objectUrl = URL.createObjectURL(blob);
			this.optimizedImageUrl = objectUrl;

		} catch (error) {
			console.error('Error fetching image with headers:', error);
			this.handleFetchError('Fetch failed');
		} finally {
			this.isFetching = false;
		}
	}

	/**
	 * Handle fetch errors by emitting error detection result and displaying fallback
	 */
	private handleFetchError(reason: string): void {
		this.isFetching = false;

		// Emit error detection result
		const errorResult: DetectionResult = {
			aspectRatio: this.displayAspectRatio,
			fallbackReason: reason,
			usedAutoDetect: false
		};

		this.detectionResult.emit(errorResult);

		// Use original image URL as fallback
		this.optimizedImageUrl = this.imageUrl;
	}
}
